import { Component, OnInit, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QRCodeComponent } from 'angularx-qrcode';

import { environment } from '../../environments/environment';
import { PaymentTerminalService } from '../services/payment-terminal.service';
import { LanguageService } from '../services/language.service';
import {
  CoinEndReason,
  KioskPhase,
  NearMatchDetection,
  PaymentDoneMessage,
  QuotedStay,
  StationConfig,
  TERMINAL_UNREACHABLE,
  VehicleDetection,
} from '../models/terminal-payment.models';

/** Cents → "€ 1,50" — the kiosk's one money format. */
export function euroLabel(cents: number | null | undefined): string {
  return `€ ${((cents ?? 0) / 100).toFixed(2).replace('.', ',')}`;
}

/** How long the station gets to confirm it opened the coin slot before the kiosk gives up on coins. */
const COIN_ACK_TIMEOUT_MS = 15_000;

/** How long the kiosk waits for the station to count and report the coins after the parker stopped. */
const COIN_REPORT_TIMEOUT_MS = 30_000;

/**
 * JJames Payment Kiosk — the single-screen state machine that drives the station display.
 *
 * All screen data comes from the backend through {@link PaymentTerminalService}: station branding,
 * the ANPR photo, the open-fee quote, and the live payment outcome (SignalR). There is intentionally
 * no hardcoded plate/fee/tariff data — a live payment terminal must never show fabricated numbers.
 *
 * Two features from the original design (typed-ahead parked-plate suggestions and ANPR near-match
 * recovery) depend on backend endpoints that do not exist yet. Their UI is kept and guarded by
 * `@if (…().length)`, so it stays hidden until a real data source is wired — it is never mocked.
 */
/** Most suggestion chips shown at once — two rows above the keyboard. */
const MAX_SUGGESTIONS = 8;

/** A suggested plate split around the typed fragment, so the template can highlight the match. */
interface PlateSuggestion {
  plate: string;
  before: string;
  match: string;
  after: string;
}

/**
 * Plates are stored and typed without separators, so this only upper-cases and drops anything that is
 * not a plate character. Keeps Ä/Ö/Ü: the keyboard has them, and stripping them would break matching.
 */
function normalizeTypedPlate(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9ÄÖÜ]/g, '');
}

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, QRCodeComponent],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit, OnDestroy {
  private readonly service = inject(PaymentTerminalService);
  readonly i18n = inject(LanguageService);

  readonly phase = signal<KioskPhase>('idle');
  readonly plate = signal('');
  readonly amountInCents = signal<number | null>(null);
  readonly currency = signal('EUR');
  readonly message = signal<string | null>(null);
  readonly receiptUrl = signal<string | null>(null);
  readonly resetSeconds = signal(environment.autoResetSeconds);
  readonly currentTime = signal('');

  // Station branding (site name, hotline, logo, languages) — loaded once on init.
  readonly stationConfig = signal<StationConfig | null>(null);

  /**
   * Type-ahead source for the plate-entry screen: the plates currently parked in this station's space,
   * loaded once from `terminal-active-plates`. The `suggestions` computed filters this locally as the parker
   * types — no per-keystroke round-trips.
   */
  private readonly parkedPlates = signal<string[]>([]);

  /** Loaded-once guard so entering the keypad (or switching language) doesn't refetch the plate list. */
  private activePlatesLoaded = false;

  /**
   * Live-filtered suggestions as the parker types. Only surfaces after 1+ chars.
   *
   * Plates that start with what was typed come first — that is how people type their own plate — then
   * plates that merely contain it, each group keeping the newest-arrival-first order the server sent.
   * Capped so the list fits two rows above the keyboard instead of scrolling off the side.
   */
  readonly suggestions = computed((): PlateSuggestion[] => {
    const typed = normalizeTypedPlate(this.plate());
    if (typed.length === 0) {
      return [];  // don't flood the screen with every plate — wait for a keystroke
    }

    const startsWith: PlateSuggestion[] = [];
    const contains: PlateSuggestion[] = [];
    for (const plate of this.parkedPlates()) {
      const at = normalizeTypedPlate(plate).indexOf(typed);
      if (at < 0) continue;
      const suggestion = {
        plate,
        before: plate.slice(0, at),
        match: plate.slice(at, at + typed.length),
        after: plate.slice(at + typed.length),
      };
      (at === 0 ? startsWith : contains).push(suggestion);
    }
    return [...startsWith, ...contains].slice(0, MAX_SUGGESTIONS);
  });

  /**
   * ANPR near-matches shown on the `notFound` recovery screen. Real fuzzy-match endpoint pending —
   * kept empty so the recovery card only ever surfaces genuine camera detections.
   */
  readonly similarDetections = signal<NearMatchDetection[]>([]);

  // Latest ANPR detection for the entered plate (confirm/correct screen).
  readonly detection = signal<VehicleDetection | null>(null);

  // Human-readable fee/tariff description echoed by the open-fee quote (`reason`), when present.
  readonly feeReason = signal<string | null>(null);
  /** The stay the current quote is about, shown on the confirm and nothing-due screens. */
  readonly quotedStay = signal<QuotedStay | null>(null);
  /** Why nothing is due: paid, covered by a right, or simply free. Picks the nothing-due screen's words. */
  readonly nothingDueKind = signal<'paid' | 'covered' | 'free'>('paid');

  private paymentRequestId: string | null = null;
  private pricedAt: string | null = null;
  private clockInterval: ReturnType<typeof setInterval> | null = null;
  private resetTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Polls the backend until the terminal confirms it received the arm.
   *
   * The reader is not asked for a card until then, so telling the parker to tap before that point is
   * an instruction they cannot act on. On 2026-09-02 the screen did exactly that for sixteen hours at
   * a column whose commands never arrived, and drivers cancelled after around thirty seconds because
   * nothing they did made anything happen.
   */
  private ackPoll: ReturnType<typeof setInterval> | null = null;

  /**
   * True once the arm has been unacknowledged long enough to be worth explaining. Kept separate from
   * the phase so the common case — an ack in well under a second — shows no extra screen at all and
   * the parker never sees a flicker.
   */
  readonly armingSlow = signal(false);

  readonly amountLabel = computed(() => euroLabel(this.amountInCents()));

  // ---------------------------------------------------------------- coins & credit

  /** Credit the quote offered for this stay (0 = none). Spent only on the parker's tap. */
  readonly creditInCents = signal(0);
  /** Credit spent on this stay by the parker's tap — shown on the success screen. */
  readonly redeemedInCents = signal(0);
  /** True when credit paid the whole fee: no fiscal receipt follows (the coins were fiscalised when taken). */
  readonly paidWithCredit = signal(false);

  readonly coinsCollected = signal(0);
  readonly coinsTarget = signal(0);
  readonly acceptedCoins = signal<number[]>([]);
  /** Coins the stay did not use, kept as the parker's credit — overpayment or a cancelled coin session. */
  readonly creditedInCents = signal(0);

  /** Coins are offered only when the station has a coin acceptor that is enabled AND reported healthy. */
  readonly coinsAvailable = computed(() => {
    const coins = this.stationConfig()?.coinAcceptor;
    return !!coins && coins.enabled && coins.online;
  });

  readonly creditLabel = computed(() => euroLabel(this.creditInCents()));
  readonly redeemedLabel = computed(() => euroLabel(this.redeemedInCents()));
  readonly creditedLabel = computed(() => euroLabel(this.creditedInCents()));
  readonly coinsCollectedLabel = computed(() => euroLabel(this.coinsCollected()));
  readonly coinsTargetLabel = computed(() => euroLabel(this.coinsTarget()));
  readonly coinsRemainingLabel = computed(() => euroLabel(Math.max(0, this.coinsTarget() - this.coinsCollected())));
  readonly acceptedCoinLabels = computed(() =>
    [...this.acceptedCoins()].sort((a, b) => b - a).map((c) => euroLabel(c)),
  );

  /** The open coin session's request id; outcomes for it are routed to {@link onCoinSessionDone}. */
  private coinRequestId: string | null = null;
  private coinAckPoll: ReturnType<typeof setInterval> | null = null;
  private coinReportTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly formattedPlate = computed(() => this.plate().trim().toUpperCase());

  /** Detection capture time formatted for the confirm screen; empty when the backend gave none. */
  readonly detectedAtLabel = computed(() => this.formatInstant(this.detection()?.detectedAt));

  /**
   * Smart 5-row Touch Keyboard Layout.
   * Numbers 1-0 on the top row, QWERTZ letters, Backspace, and Search CTA. No space or hyphen key:
   * plates are stored without separators, so a typed separator could only make the plate miss.
   */
  readonly keypadRows = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
    ['DEL', 'Y', 'X', 'C', 'V', 'B', 'N', 'M', 'Ü'],
    ['OK'],
  ];

  readonly isNumberKey = (key: string): boolean => '0123456789'.includes(key);

  /**
   * What the receipt QR encodes: the fiscal receipt URL pushed over the hub (task 6.4).
   *
   * Null until EFSTA answers — the success screen shows the "generating" spinner meanwhile. The QR itself is
   * rendered locally by `angularx-qrcode` (same lib as the operator-app Easypass pass), never by a third-party
   * image service: a station may have no route to the open internet, and the receipt URL must not leak.
   */
  readonly receiptQrData = computed(() => this.receiptUrl());

  constructor() {
    // Terminal outcome pushed over the hub → success / failed.
    effect(() => {
      const done = this.service.paymentDone();
      if (!done) return;

      if (this.coinRequestId && done.paymentRequestId === this.coinRequestId) {
        this.onCoinSessionDone(done);
        return;
      }
      if (done.paymentRequestId !== this.paymentRequestId) return;

      if (done.outcome === 'Paid') {
        this.phase.set('success');
        this.message.set(null);
        this.startAutoResetTimer();
      } else if (done.statusReason === TERMINAL_UNREACHABLE) {
        // The command never reached the reader — the card was never asked for, so this is not a
        // payment failure and must not be shown as one. Medicent, 2026-09-02: eighteen attempts died
        // this way and the screen simply kept asking for a card that could never be read, until the
        // driver gave up. Retrying is free now: the backend releases the stay as soon as an
        // unacknowledged attempt is cancelled, instead of holding it for five minutes.
        this.phase.set('unreachable');
        this.message.set(null);
        this.startAutoResetTimer();
      } else {
        this.phase.set('failed');
        // Deliberately NOT done.statusReason: that is the terminal's technical text
        // ("terminal rejected the authorization…"), which means nothing to someone
        // standing at a column and only worries them. Show the short outcome line.
        // The reason is still logged/available backend-side for support.
        this.message.set(this.outcomeMessage(done.outcome));
        this.startAutoResetTimer();
      }
    });

    // A coin landed → move the running total. Display only; the money is booked from the Pi's report.
    effect(() => {
      const progress = this.service.coinProgress();
      if (!progress || progress.paymentRequestId !== this.coinRequestId) return;
      this.coinsCollected.set(progress.collectedInCents);
      // A coin proves the slot is open even if the acknowledgement poll has not caught up yet.
      if (this.phase() === 'coinsStarting') {
        this.stopCoinAckPoll();
        this.phase.set('coins');
      }
    });

    // Fiscal receipt URL pushed over the hub → render the QR.
    effect(() => {
      const receipt = this.service.fiscalReceipt();
      if (receipt && receipt.paymentRequestId === this.paymentRequestId) {
        this.receiptUrl.set(receipt.receiptUrl);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.updateClock();
    this.clockInterval = setInterval(() => this.updateClock(), 1000);

    await this.service.connect();

    try {
      const cfg = await this.service.stationConfig();
      this.stationConfig.set(cfg);
      // Initialize the default language from station config (falls back to de/en in the service).
      if (cfg.defaultLanguage) {
        this.i18n.set(cfg.defaultLanguage === 'en' ? 'en' : 'de');
      }
    } catch {
      // Branding is presentation-only — degrade to the built-in defaults if it can't load.
    }
  }

  ngOnDestroy(): void {
    if (this.clockInterval) clearInterval(this.clockInterval);
    if (this.resetTimer) clearInterval(this.resetTimer);
    this.stopAckPoll();
    this.stopCoinWatch();
  }

  private updateClock(): void {
    const now = new Date();
    const dateStr = now.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
    this.currentTime.set(`${dateStr} | ${timeStr}`);
  }

  /** Format an ISO instant as `dd.MM.yyyy, HH:mm` (de-AT). Returns '' for null/unparseable input. */
  private formatInstant(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Hold the "preparing the reader" screen until the terminal says it got the command, then show
   * "tap your card".
   *
   * <b>Why not go straight to tap.</b> Arming is a push into a SignalR group, and a group send reports
   * success whether or not anyone is listening — so "the backend accepted it" has never meant "the
   * reader knows". `terminalAckedAt` is the first signal that distinguishes the two, and this is what
   * puts it in front of the person standing at the column.
   *
   * Nothing here decides the outcome. If the command never lands, the backend gives up at the arming
   * window and pushes PaymentDone with TerminalUnreachable, which the hub effect turns into the
   * unreachable screen. This poll only decides what the parker is looking at meanwhile.
   */
  private awaitTerminalAck(paymentRequestId: string): void {
    this.stopAckPoll();
    this.armingSlow.set(false);

    const startedAt = Date.now();
    const intervalMs = 1000;

    // Show "still connecting" only after a couple of seconds. A healthy column acks immediately, and
    // announcing a problem that resolves itself in 400ms teaches parkers to distrust the machine.
    const explainAfterMs = 2500;

    const check = async (): Promise<void> => {
      // The parker moved on — cancelled, or the hub already reported an outcome.
      if (this.phase() !== 'arming' || this.paymentRequestId !== paymentRequestId) {
        this.stopAckPoll();
        return;
      }

      if (Date.now() - startedAt >= explainAfterMs) this.armingSlow.set(true);

      try {
        const status = await this.service.status(paymentRequestId);
        if (this.phase() !== 'arming' || this.paymentRequestId !== paymentRequestId) {
          this.stopAckPoll();
          return;
        }

        if (status.terminalAckedAt) {
          this.stopAckPoll();
          this.armingSlow.set(false);
          this.phase.set('tap');
        }
      } catch {
        // A failed poll says nothing about the terminal — only that this one request did not land.
        // Keep waiting: the backend's arming window is the authority on giving up, not our network.
      }
    };

    // Checked once straight away, not only on the first tick. The overwhelming majority of arms are
    // acknowledged in well under a second, and making every one of those parkers wait a full interval
    // for "tap your card" would trade a real fix for a visible delay on every single payment.
    void check();
    this.ackPoll = setInterval(check, intervalMs);
  }

  private stopAckPoll(): void {
    if (this.ackPoll) clearInterval(this.ackPoll);
    this.ackPoll = null;
  }

  private startAutoResetTimer(): void {
    if (this.resetTimer) clearInterval(this.resetTimer);
    this.resetSeconds.set(environment.autoResetSeconds);
    this.resetTimer = setInterval(() => {
      const current = this.resetSeconds();
      if (current <= 1) {
        if (this.resetTimer) clearInterval(this.resetTimer);
        this.reset();
      } else {
        this.resetSeconds.set(current - 1);
      }
    }, 1000);
  }

  // ---------------------------------------------------------------- idle

  wakeFromIdle(): void {
    this.phase.set('entry');
    void this.loadActivePlates();
    // Coin acceptor health can change between parkers (jam, unplugged). One read per session keeps
    // the coin button honest without polling while the kiosk idles.
    void this.refreshStationConfig();
  }

  private async refreshStationConfig(): Promise<void> {
    try {
      this.stationConfig.set(await this.service.stationConfig());
    } catch {
      // Keep the last good config — branding and coin availability degrade, never block.
    }
  }

  /** Load this station's active plates once per session, so the entry screen can offer them as type-ahead suggestions. */
  private async loadActivePlates(): Promise<void> {
    if (this.activePlatesLoaded) return;
    try {
      this.parkedPlates.set(await this.service.activeParkedPlates());
      this.activePlatesLoaded = true;
    } catch {
      this.parkedPlates.set([]);
      // Don't set activePlatesLoaded=true on failure — retry on next wake.
    }
  }

  get availableLangs(): { code: string; label: string }[] {
    const langs = this.stationConfig()?.languages?.length
      ? this.stationConfig()!.languages
      : ['de', 'en'];
    const labels: Record<string, string> = { de: 'Deutsch', en: 'English' };
    return langs
      .filter((c) => c === 'de' || c === 'en')
      .map((code) => ({ code, label: labels[code] ?? code.toUpperCase() }));
  }

  switchLang(lang: string): void {
    this.i18n.set(lang === 'en' ? 'en' : 'de');
    this.wakeFromIdle();
  }

  // ---------------------------------------------------------------- keypad & autocomplete

  press(key: string): void {
    if (this.phase() !== 'entry') return;

    if (key === 'DEL') {
      this.plate.update((p) => p.slice(0, -1));
    } else if (key === 'OK') {
      void this.onPlateConfirmed();
    } else if (this.plate().length < 12) {
      this.plate.update((p) => p + key.toUpperCase());
    }
  }

  selectSuggestion(p: string): void {
    this.plate.set(p);
    void this.quote(true);  // from suggestion: we know the plate is parked
  }

  // ---------------------------------------------------------------- detection & notFound

  /**
   * Entry → quote first. An already-paid plate short-circuits before any confirmation (Option A): there is
   * nothing to charge, so asking "is this your vehicle?" is pointless friction. Only a payable plate goes on
   * to the ANPR confirm and then the amount screen.
   */
  async onPlateConfirmed(): Promise<void> {
    const plate = this.formattedPlate();
    if (!plate) return;
    await this.quote(false);  // typed manually: may or may not be parked
  }

  /** ANPR confirmed — the fee is already quoted, so go straight to the amount confirmation. */
  confirmDetection(): void {
    this.phase.set('confirm');
  }

  /** Fetch the ANPR photo and show the confirm/correct screen; skip straight to amount when none exists. */
  private async showDetection(plate: string): Promise<void> {
    try {
      this.detection.set(await this.service.vehicleDetection(plate));
      this.phase.set('detection');
    } catch {
      this.detection.set(null);
      this.phase.set('confirm');
    }
  }

  /** "Correct plate" from the detection screen — keep the typed value so the parker can edit it. */
  startCorrection(): void {
    this.phase.set('entry');
  }

  selectSimilarPlate(sim: NearMatchDetection): void {
    // Capture the original typed plate BEFORE we overwrite it with the near-match.
    const typedPlate = this.formattedPlate();
    this.plate.set(sim.plate);

    // Correct the ANPR record: the parking process for the near-match plate
    // (camera misread) should be reassigned to the plate the parker actually typed.
    if (sim.parkingProcessId && typedPlate) {
      this.service.correctPlate(sim.parkingProcessId, typedPlate).catch(() => {
        // Best-effort — payment proceeds regardless.
      });
    }
    void this.quote(true);  // from near-match: we know the plate is parked
  }

  /** "Correct plate" from the notFound screen — return to the keypad with the typed value intact. */
  editOcrMisread(): void {
    this.phase.set('entry');
  }

  // ---------------------------------------------------------------- steps

  /**
   * Short, parker-facing text for a terminal outcome — never the raw technical reason.
   * `t()` echoes the key back when there is no translation, so an unknown outcome falls
   * through to the generic line instead of printing "outcome.Whatever" on the screen.
   */
  private outcomeMessage(outcome: string | null | undefined): string {
    const key = `outcome.${outcome}`;
    const text = this.i18n.t(key);
    return text === key ? this.i18n.t('outcome.default') : text;
  }

  async quote(fromSuggestion = false): Promise<void> {
    const plate = this.formattedPlate();
    if (!plate) return;

    this.phase.set('quoting');
    this.message.set(null);

    try {
      const fee = await this.service.quote(plate);
      this.quotedStay.set(fee.stay);

      if (fee.alreadyPaid) {
        // Nothing to charge — show a benign "already paid" screen, no vehicle confirmation (Option A).
        this.nothingDueKind.set('paid');
        this.phase.set('alreadyPaid');
        this.startAutoResetTimer();
        return;
      }
      if (fee.isCovered) {
        // Easypass, Dauerparker, pool: the stay was found and a right covers it. Never "already
        // paid" — nobody charged this parker anything.
        this.nothingDueKind.set('covered');
        this.phase.set('alreadyPaid');
        this.startAutoResetTimer();
        return;
      }
      if (!fee.hasOpenFee || fee.amountInCents == null) {
        if (fromSuggestion) {
          // The plate IS parked (it came from the active-plates list) but has no open fee.
          // Don't ask "is this your car?" when we already know they're here; just tell them and reset.
          this.nothingDueKind.set('free');
          this.phase.set('alreadyPaid');
          this.startAutoResetTimer();
          return;
        }
        // Manually typed plate — genuinely not found, or parked with no fee.
        // Show the recovery screen with near-matches so the parker can correct.
        await this.loadNearMatches(plate);
        this.phase.set('notFound');
        return;
      }

      this.amountInCents.set(fee.amountInCents);
      this.currency.set(fee.currency ?? 'EUR');
      this.feeReason.set(fee.reason);
      this.pricedAt = fee.pricedAt;
      this.creditInCents.set(fee.redeemableCreditInCents);
      // There's a fee — now show the ANPR photo so the parker can confirm/correct before paying.
      await this.showDetection(plate);
    } catch {
      // A backend/network failure is NOT "plate not found". Routing it to the notFound
      // screen tells the parker their plate is unknown when in fact we never asked —
      // so they retype it, or give up and drive off unpaid. Show the service-unavailable
      // screen instead, which offers Retry (re-runs quote() with the same plate).
      this.phase.set('failed');
      this.message.set(this.i18n.t('error.service'));
      this.startAutoResetTimer();
    }
  }

  /** Populate the recovery screen with real near-matches for the plate; empty on failure (card stays hidden). */
  private async loadNearMatches(plate: string): Promise<void> {
    try {
      this.similarDetections.set(await this.service.nearMatches(plate));
    } catch {
      this.similarDetections.set([]);
    }
  }

  async pay(): Promise<void> {
    this.phase.set('arming');
    try {
      const started = await this.service.start(
        this.formattedPlate(),
        this.amountInCents() ?? undefined,
        this.pricedAt,
      );
      this.paymentRequestId = started.paymentRequestId;
      this.amountInCents.set(started.amountInCents);
      this.currency.set(started.currency);
      this.awaitTerminalAck(started.paymentRequestId);
    } catch (err) {
      // A price that moved between quote and confirm is the NORMAL case at a free-flow site:
      // the stay is priced to now, so any parker who reads the amount, finds their card and
      // taps after a billing block boundary crosses it. The backend refuses to arm on a stale
      // amount (rightly — it will not charge a number nobody agreed to), but sending the parker
      // to a dead-end error for it strands them at the column with no path but starting over.
      // Re-quote and let them confirm the new figure instead.
      if (await this.requoteAfterPriceChange(err)) return;

      this.phase.set('failed');
      this.message.set(this.i18n.t('error.armFailed'));
    }
  }

  /**
   * Recover from the backend's "Amount Mismatch" refusal by fetching the current price and
   * returning to the confirm screen. Returns false when this was some other failure, or when
   * the re-quote itself fails — the caller then shows the ordinary error.
   *
   * The parker always re-confirms: silently charging a different amount than the one on screen
   * is the one thing worse than the error message this replaces.
   */
  private async requoteAfterPriceChange(err: unknown): Promise<boolean> {
    const status = (err as { status?: number } | null)?.status;
    const body = (err as { error?: unknown } | null)?.error;
    // MadResult renders a 400 body as `Title + "." + Detail`, so the title is a plain substring.
    const text = typeof body === 'string' ? body : JSON.stringify(body ?? '');
    if (status !== 400 || !text.includes('Amount Mismatch')) return false;

    try {
      const fee = await this.service.quote(this.formattedPlate());
      if (!fee.hasOpenFee || fee.amountInCents == null) return false;
      this.quotedStay.set(fee.stay);

      this.amountInCents.set(fee.amountInCents);
      this.currency.set(fee.currency ?? 'EUR');
      this.feeReason.set(fee.reason);
      this.pricedAt = fee.pricedAt;
      this.creditInCents.set(fee.redeemableCreditInCents);
      this.message.set(this.i18n.t('error.priceChanged'));
      this.phase.set('confirm');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Parker-initiated cancel/restart. Resets the UI immediately — no point making the
   * parker wait for the backend to confirm an abort that is best-effort by design.
   * The abort HTTP call fires in the background; a late `Paid` still arrives over the
   * hub and is handled by the outcome path regardless.
   */
  cancel(): void {
    const requestId = this.paymentRequestId;
    const armed = !!requestId && (this.phase() === 'tap' || this.phase() === 'arming');
    const coinId = this.coinRequestId;
    const coinsOpen = !!coinId && ['coinsStarting', 'coins', 'coinsStopping'].includes(this.phase());

    this.reset();

    if (armed) {
      this.service.abort(requestId!).catch(() => {
        // Reader may already be idle, or the link is flaky — irrelevant, UI already reset.
      });
    }
    if (coinsOpen) {
      // Close the slot. Whatever was inserted is still booked — as credit, since the stay was not paid.
      this.service.stopCoins(coinId!, 'Cancel').catch(() => { /* the backend sweeper closes it */ });
    }
  }

  // ---------------------------------------------------------------- credit

  /**
   * The parker chose to pay with credit. Never automatic: this runs only from the button, and never
   * spends more than the amount shown on it.
   */
  async payWithCredit(): Promise<void> {
    const credit = this.creditInCents();
    if (credit <= 0) return;

    this.phase.set('redeeming');
    this.message.set(null);
    try {
      const result = await this.service.redeemCredit(newRedemptionId(), this.formattedPlate(), credit, this.pricedAt);
      this.redeemedInCents.set(result.redeemedInCents);

      if (result.covered) {
        this.paidWithCredit.set(true);
        this.phase.set('success');
        this.startAutoResetTimer();
        return;
      }
      await this.backToConfirm(`${this.i18n.t('credit.used')}: ${euroLabel(result.redeemedInCents)}`);
    } catch {
      this.message.set(this.i18n.t('credit.failed'));
      this.phase.set('confirm');
    }
  }

  // ---------------------------------------------------------------- coins

  /** The parker chose coins: open the slot for the amount on screen. */
  async payWithCoins(): Promise<void> {
    this.phase.set('coinsStarting');
    this.message.set(null);
    this.coinsCollected.set(0);
    this.creditedInCents.set(0);

    try {
      const started = await this.service.startCoins(
        this.formattedPlate(),
        this.amountInCents() ?? undefined,
        this.pricedAt,
      );
      this.coinRequestId = started.paymentRequestId;
      this.coinsTarget.set(started.amountInCents);
      this.amountInCents.set(started.amountInCents);
      this.acceptedCoins.set(started.acceptedCoinValuesInCents);
      this.awaitCoinAck(started.paymentRequestId);
    } catch (err) {
      if (await this.requoteAfterPriceChange(err)) return;
      this.markCoinsUnavailable();
      this.message.set(this.i18n.t('coins.unavailable'));
      this.phase.set('confirm');
    }
  }

  /** "Rest mit Karte" or "Abbrechen" on the coin screen. The station counts and reports the coins. */
  stopCoins(reason: Extract<CoinEndReason, 'SwitchToCard' | 'Cancel'>): void {
    const id = this.coinRequestId;
    if (!id || this.phase() !== 'coins') return;

    this.phase.set('coinsStopping');
    this.service.stopCoins(id, reason).catch(() => { /* the timeout below covers a lost stop */ });

    // The station reports every session, but one that died mid-session cannot. Coins it took are
    // still booked when it comes back (as credit); the parker must not be held here waiting for it.
    this.coinReportTimeout = setTimeout(() => {
      if (this.phase() !== 'coinsStopping' || this.coinRequestId !== id) return;
      this.coinRequestId = null;
      this.markCoinsUnavailable();
      this.message.set(this.i18n.t('coins.notReported'));
      this.phase.set('failed');
      this.startAutoResetTimer();
    }, COIN_REPORT_TIMEOUT_MS);
  }

  /** The station reported the coin session. Where the coins went was decided by the backend. */
  private onCoinSessionDone(done: PaymentDoneMessage): void {
    const coinId = this.coinRequestId;
    this.stopCoinWatch();
    this.coinRequestId = null;

    const credited = done.creditedInCents ?? 0;
    this.coinsCollected.set(done.collectedInCents ?? this.coinsCollected());
    this.creditedInCents.set(credited);

    switch (done.coinEndReason) {
      case 'TargetReached':
        this.paymentRequestId = coinId; // the coin receipt (EFSTA) is pushed for this request id
        this.phase.set('success');
        this.startAutoResetTimer();
        return;

      case 'SwitchToCard':
        this.paymentRequestId = coinId;
        if ((done.remainingDueInCents ?? 1) <= 0) {
          this.phase.set('success');
          this.startAutoResetTimer();
        } else {
          void this.continueWithCard();
        }
        return;

      case 'Fault':
        // The parker is still here and still owes the fee: back to the choice, coins hidden.
        this.markCoinsUnavailable();
        void this.backToConfirm(
          credited > 0
            ? `${this.i18n.t('coins.fault')} ${euroLabel(credited)} ${this.i18n.t('coins.savedAsCredit')}`
            : this.i18n.t('coins.fault'),
        );
        return;

      default:
        // Cancel, walked away (InactivityTimeout), daemon restart: coins taken are the parker's credit.
        if (credited > 0) {
          this.phase.set('coinsCredited');
          this.startAutoResetTimer();
        } else {
          this.reset();
        }
    }
  }

  /** Coins paid part of the stay; arm the card for what is left (the quote now nets the coins off). */
  private async continueWithCard(): Promise<void> {
    this.phase.set('quoting');
    try {
      const fee = await this.service.quote(this.formattedPlate());
      if (!fee.hasOpenFee || fee.amountInCents == null) {
        this.phase.set('success');
        this.startAutoResetTimer();
        return;
      }
      this.amountInCents.set(fee.amountInCents);
      this.pricedAt = fee.pricedAt;
      this.creditInCents.set(fee.redeemableCreditInCents);
      await this.pay();
    } catch {
      this.phase.set('failed');
      this.message.set(this.i18n.t('error.service'));
      this.startAutoResetTimer();
    }
  }

  /** Re-quote and return to the confirm screen with a notice, after credit or coins changed what is due. */
  private async backToConfirm(notice: string): Promise<void> {
    this.phase.set('quoting');
    try {
      const fee = await this.service.quote(this.formattedPlate());
      if (!fee.hasOpenFee || fee.amountInCents == null) {
        this.phase.set('success');
        this.startAutoResetTimer();
        return;
      }
      this.amountInCents.set(fee.amountInCents);
      this.pricedAt = fee.pricedAt;
      this.creditInCents.set(fee.redeemableCreditInCents);
      this.message.set(notice);
      this.phase.set('confirm');
    } catch {
      this.phase.set('failed');
      this.message.set(this.i18n.t('error.service'));
      this.startAutoResetTimer();
    }
  }

  /**
   * Show "insert coins" only once the station confirms it opened the slot, the same rule as the card
   * ("tap your card" only after the ack). A station that never confirms is given up on: the session is
   * cancelled (an unacknowledged one closes at once) and the parker pays by card instead.
   */
  private awaitCoinAck(paymentRequestId: string): void {
    this.stopCoinAckPoll();
    const startedAt = Date.now();

    const check = async (): Promise<void> => {
      if (this.phase() !== 'coinsStarting' || this.coinRequestId !== paymentRequestId) {
        this.stopCoinAckPoll();
        return;
      }
      if (Date.now() - startedAt >= COIN_ACK_TIMEOUT_MS) {
        this.stopCoinAckPoll();
        this.coinRequestId = null;
        this.service.stopCoins(paymentRequestId, 'Cancel').catch(() => { /* swept by the backend */ });
        this.markCoinsUnavailable();
        this.message.set(this.i18n.t('coins.unavailable'));
        this.phase.set('confirm');
        return;
      }
      try {
        const status = await this.service.status(paymentRequestId);
        if (status.terminalAckedAt && this.phase() === 'coinsStarting' && this.coinRequestId === paymentRequestId) {
          this.stopCoinAckPoll();
          this.phase.set('coins');
        }
      } catch {
        // One failed poll says nothing about the station; the timeout above is the authority.
      }
    };

    void check();
    this.coinAckPoll = setInterval(check, 1000);
  }

  /** Hide coins for the rest of this session; the next wake re-reads the station's real state. */
  private markCoinsUnavailable(): void {
    const cfg = this.stationConfig();
    if (cfg?.coinAcceptor) {
      this.stationConfig.set({ ...cfg, coinAcceptor: { ...cfg.coinAcceptor, online: false } });
    }
  }

  private stopCoinAckPoll(): void {
    if (this.coinAckPoll) clearInterval(this.coinAckPoll);
    this.coinAckPoll = null;
  }

  private stopCoinWatch(): void {
    this.stopCoinAckPoll();
    if (this.coinReportTimeout) clearTimeout(this.coinReportTimeout);
    this.coinReportTimeout = null;
  }

  reset(): void {
    if (this.resetTimer) clearInterval(this.resetTimer);
    this.stopAckPoll();
    this.armingSlow.set(false);
    this.phase.set('idle');
    this.plate.set('');
    this.amountInCents.set(null);
    this.currency.set('EUR');
    this.message.set(null);
    this.receiptUrl.set(null);
    this.feeReason.set(null);
    this.quotedStay.set(null);
    this.nothingDueKind.set('paid');
    this.paymentRequestId = null;
    this.pricedAt = null;
    this.detection.set(null);
    this.similarDetections.set([]);
    this.parkedPlates.set([]);
    this.activePlatesLoaded = false;
    this.service.paymentDone.set(null);
    this.service.fiscalReceipt.set(null);
    this.service.coinProgress.set(null);
    this.stopCoinWatch();
    this.coinRequestId = null;
    this.creditInCents.set(0);
    this.redeemedInCents.set(0);
    this.paidWithCredit.set(false);
    this.coinsCollected.set(0);
    this.coinsTarget.set(0);
    this.acceptedCoins.set([]);
    this.creditedInCents.set(0);
  }
}

/** One id per credit tap: a retried request with the same id spends nothing twice. */
function newRedemptionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Non-secure contexts lack randomUUID; a v4-shaped random id is enough for an idempotency key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
